/**
 * client/audio/alias.ts — BO2 sound-alias maths (pure, no Web Audio): manifest types, variant
 * pick, dB / cents conversion, falloff curves, distance gains, voice-limit policies.
 *
 * OWNER: HUD-UI-AUDIO agent.
 *
 * Source: game/public/assets/audio/manifest.json, semantics in research/audio.md §3.1 (from OAT
 * SndBankDumperT6 / CommonT6):
 *  - volDb is dBspl with 100 = full scale: linear = 10^((dB-100)/20); a random value in [min,max]
 *    per play (ESTIMATE from the field naming).
 *  - pitchCents: rate = 2^(cents/1200), random in [min,max].
 *  - Distances in game units. Inside distMin the gain follows curve.dryMin evaluated at d/distMin;
 *    from distMin to distMaxDry it follows curve.dryMax at (d-distMin)/(distMaxDry-distMin); beyond
 *    distMaxDry it is silent (unless ignoreMaxDist). The wet send does the same with wetMin/wetMax
 *    and distMaxWet. This "min curve inside distMin" reading is an ESTIMATE that makes the close /
 *    dist layer pairs cross-fade (wpn_crossbow_fire_npc: dryMin cosdelay 1->0 over 0..250 and
 *    dryMax alloff; its secondary wpn_crossbow_fire_dist: dryMin rcurve0 0->1 over 0..250).
 *  - limitCount / limitType (priority | oldest | reject) cap global instances of an alias;
 *    entityLimitCount / entityLimitType do the same per entity.
 *  - globals.volumeGroups[g].attenuationMp: the MP mix gain of the alias' volume group
 *    (group.csv attenuationMp / 65535). Only the alias' own group is applied (parents are UI
 *    slider categories) - ESTIMATE.
 */

export type Curve = readonly (readonly [number, number])[];

export interface AliasVariant {
  file: string;
  dur: number;
  ch: number;
  hz: number;
}

export interface AliasDef {
  variants: AliasVariant[];
  bus: string;
  volumeGroup: string;
  volDb: [number, number];
  vol: [number, number];
  pitchCents: [number, number];
  rate: [number, number];
  distMin: number;
  distMaxDry: number;
  distMaxWet: number;
  curve: { dryMin: string; dryMax: string; wetMin: string; wetMax: string };
  reverbSendDb: number;
  limitCount: number;
  limitType: 'priority' | 'oldest' | 'reject' | string;
  entityLimitCount: number;
  entityLimitType: 'priority' | 'oldest' | 'reject' | string;
  priority: [number, number];
  pan: '2d' | '3d' | string;
  looping: boolean;
  randomize: string;
  probability: number;
  startDelayMs: number;
  isMusic: boolean;
  fadeInMs: number;
  fadeOutMs: number;
  ignoreMaxDist: boolean;
  secondary: string | null;
  timescale?: boolean;
  storage?: string;
}

export interface AudioManifest {
  formats: string[];
  globals: {
    curves: Record<string, Curve>;
    volumeGroups: Record<string, { attenuationMp: number; category: string; parent: string | null }>;
  };
  musicStates: Record<string, { op: string; alias?: string; exists?: boolean; args?: number[] }[]>;
  map: Record<string, {
    rooms: Record<string, { default: boolean; tone?: { alias: string; fadeIn: number; fadeOut: number }; reverb: string; context: string[] }>;
    loopEmitters: { alias: string; origin: [number, number, number] }[];
    autoFx: { fx: string; alias: string; args: number[] }[];
  }>;
  aliases: Record<string, AliasDef>;
}

/** dBspl (100 = full scale) -> linear gain. */
export function dbToLinear(db: number): number {
  return Math.pow(10, (db - 100) / 20);
}

export function centsToRate(cents: number): number {
  return Math.pow(2, cents / 1200);
}

/** Uniform pick in [min, max] with rnd in [0,1). */
export function randRange(r: readonly [number, number], rnd: number): number {
  return r[0] + (r[1] - r[0]) * rnd;
}

/** Variant index for rnd in [0,1); never out of range. */
export function pickVariant(count: number, rnd: number): number {
  if (count <= 0) return -1;
  const i = Math.floor(rnd * count);
  return i < 0 ? 0 : i >= count ? count - 1 : i;
}

/**
 * Piecewise-linear falloff curve at x in [0,1]. Knots may repeat an x (step); the first knot
 * whose x reaches the input wins on the right side of a step, so allon/defaultmin stay 1 up to
 * and including x = 1 - eps and reach their last value only exactly at x = 1.
 */
export function evalCurve(knots: Curve | undefined, x: number): number {
  if (!knots || knots.length === 0) return 1;
  if (x <= knots[0]![0]) return knots[0]![1];
  const last = knots[knots.length - 1]!;
  if (x >= last[0]) {
    // value at exactly x = 1 is the last knot; inputs > 1 clamp to it
    return x > last[0] ? last[1] : valueAtExactEnd(knots);
  }
  for (let i = 1; i < knots.length; i++) {
    const a = knots[i - 1]!, b = knots[i]!;
    if (x <= b[0]) {
      if (b[0] === a[0]) return b[1];
      return a[1] + ((b[1] - a[1]) * (x - a[0])) / (b[0] - a[0]);
    }
  }
  return last[1];
}
function valueAtExactEnd(knots: Curve): number {
  return knots[knots.length - 1]![1];
}

export interface DistGains { dry: number; wet: number }

/** Distance attenuation (dry + wet) for a 3D alias at distance d (game units). */
export function distanceGains(a: AliasDef, curves: Record<string, Curve>, d: number, out: DistGains = { dry: 1, wet: 1 }): DistGains {
  out.dry = segmentGain(d, a.distMin, a.distMaxDry, curves[a.curve.dryMin], curves[a.curve.dryMax], a.ignoreMaxDist);
  out.wet = segmentGain(d, a.distMin, a.distMaxWet, curves[a.curve.wetMin], curves[a.curve.wetMax], a.ignoreMaxDist);
  return out;
}

function segmentGain(d: number, min: number, max: number, minCurve: Curve | undefined, maxCurve: Curve | undefined, ignoreMax: boolean): number {
  if (d < 0) d = 0;
  if (min > 0 && d < min) return clamp01(evalCurve(minCurve, d / min));
  if (max <= min) return d <= min ? 1 : ignoreMax ? clamp01(evalCurve(maxCurve, 1)) : 0;
  if (d >= max) return ignoreMax ? clamp01(evalCurve(maxCurve, 1)) : 0;
  return clamp01(evalCurve(maxCurve, (d - min) / (max - min)));
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** One live instance, as seen by the limiter. */
export interface LimitVoice {
  id: number;
  priority: number;
  start: number;
}

export type LimitDecision = { play: false } | { play: true; steal: number | null };

/**
 * Voice-limit policy for `count` = limitCount:
 *  - reject: the new sound is dropped while `count` instances play;
 *  - oldest: the oldest instance is stopped to make room;
 *  - priority: the lowest-priority instance (oldest on ties) is stopped if its priority is <= the
 *    new sound's, otherwise the new sound is dropped.
 * count <= 0 means unlimited.
 */
export function limitDecision(active: readonly LimitVoice[], count: number, type: string, newPriority: number): LimitDecision {
  if (count <= 0 || active.length < count) return { play: true, steal: null };
  if (type === 'reject') return { play: false };
  if (type === 'oldest') {
    let o = active[0]!;
    for (const v of active) if (v.start < o.start) o = v;
    return { play: true, steal: o.id };
  }
  // priority
  let lo = active[0]!;
  for (const v of active) if (v.priority < lo.priority || (v.priority === lo.priority && v.start < lo.start)) lo = v;
  if (lo.priority <= newPriority) return { play: true, steal: lo.id };
  return { play: false };
}

/** Linear gain of one play: random volume in the alias range x volume-group attenuation. */
export function aliasGain(a: AliasDef, groups: AudioManifest['globals']['volumeGroups'], rnd: number): number {
  const v = a.vol ? randRange(a.vol, rnd) : dbToLinear(randRange(a.volDb, rnd));
  const g = groups[a.volumeGroup]?.attenuationMp ?? 1;
  return v * g;
}

/** Bolt beep times (s, relative to arming) for _explosive_bolt.csc fx_think until `fuseS`. */
export function boltBeepTimes(fuseS: number, startS = 0.3, div = 1.2, minS = 0.08): number[] {
  const out: number[] = [];
  let t = 0, interval = startS;
  while (t < fuseS - 1e-6) {
    out.push(t);
    t += interval;
    interval = Math.min(startS, Math.max(minS, interval / div));
  }
  return out;
}
